import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { getDate } from "@utils/helperFunctions";
import {
  useClientsList,
  useClientMutations,
  useDownloadClientsCsv,
} from "../api";
import { buildClientBatchActions, type ClientTab } from "./batchActions";
import { MergeClientsModal } from "../MergeClientsModal/MergeClientsModal";
import type { Client } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ClientTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const sel = useBatchSelection([tab]);
  const { archive, restore, merge } = useClientMutations();
  const downloadCsv = useDownloadClientsCsv();
  const [downloading, setDownloading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } = useClientsList({
    page: { number: pageNumber, size: pageSize },
    filter: {
      active: tab === "active",
      ...(nameFilter ? { name: nameFilter } : {}),
    },
    sort: ["name"],
  });

  const selectedClients = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Client>[] = [
    {
      accessorKey: "name",
      header: "Client Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "msaDate",
      header: "MSA Date",
      cell: ({ row }) => getDate(row.original.msaDate) ?? "",
    },
    {
      accessorKey: "domain",
      header: "Domain",
      cell: ({ row }) => row.original.domain ?? "",
    },
  ];

  const runOnSelected = async (
    fn: (c: Client) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selectedClients];
    let failures = 0;
    for (const c of targets) {
      try {
        await fn(c);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} client(s).`,
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (c) => archive(c.id),
        (n) => `${n} client(s) archived.`
      );
    } else {
      await runOnSelected(
        (c) => restore(c.id, c.name),
        (n) => `${n} client(s) restored.`
      );
    }
  };

  const handleConfirmMerge = async (targetId: string) => {
    setMergeOpen(false);
    const sources = selectedClients.filter((c) => c.id !== targetId);
    if (sources.length === 0) {
      sel.clear();
      return;
    }
    let failures = 0;
    for (const s of sources) {
      try {
        await merge(s.id, targetId);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: `Merged ${sources.length} client(s).` }
        : {
            variant: "danger",
            text: `Failed to merge ${failures} of ${sources.length} client(s).`,
          }
    );
    sel.clear();
    refetch();
  };

  const batchActions = buildClientBatchActions(tab, sel.selectedIds.length, {
    onDelete: () => setConfirmOpen(true),
    onRestore: () => setConfirmOpen(true),
    onMerge: () => setMergeOpen(true),
  });

  const submitSearch = () => {
    setPageNumber(1);
    setNameFilter(searchInput);
  };

  const handleDownloadCsv = async () => {
    setDownloading(true);
    try {
      await downloadCsv();
    } catch {
      setToast({ variant: "danger", text: "Could not download clients CSV." });
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Clients"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Client"
        secondaryAction={{
          label: "Download CSV",
          onClick: handleDownloadCsv,
          disabled: downloading,
        }}
        searchProps={{
          label: "Search Clients",
          placeholder: "Search by client name",
          value: searchInput,
          onChange: (e) => setSearchInput(e.target.value),
          onSubmit: (e) => {
            e.preventDefault();
            submitSearch();
          },
          onClear: () => {
            setSearchInput("");
            setPageNumber(1);
            setNameFilter("");
          },
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as ClientTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No clients found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={tab === "active" ? "Archive clients?" : "Restore clients?"}
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} client(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      <MergeClientsModal
        open={mergeOpen}
        clients={selectedClients}
        onConfirm={handleConfirmMerge}
        onCancel={() => setMergeOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
